/**
 * PaymentProof API Service
 */

import { commonServerInstance, downloadInstance } from '@/utilities/instances'
import { downloadFile } from '@/service/common'
import type {
  IPaymentFundsParams,
  IPaymentFundsResponse,
  IPaymentFundsResponseData,
  IPaymentFundsPagination,
  IPaymentFund,
  IAddFundsPayload,
  IOpenInvoicesResponse,
  IAllocationResult,
  IPrepareAllocationsPayload,
  IExportPaymentFundsParams,
  IAllocationHistoryParams,
  IAllocationHistoryResponse,
  IExportAllocationHistoryParams
} from '@/pages/PaymentProof/types'

const BASE_URL = '/api/payment-proofs'

export const fetchPaymentFunds = async (
  params: IPaymentFundsParams
): Promise<IPaymentFundsResponse> => {
  // delete params which is undefined or null or empty string
  const filteredParams = Object.keys(params).reduce((acc, key) => {
    if (
      params[key as keyof IPaymentFundsParams] !== undefined &&
      params[key as keyof IPaymentFundsParams] !== null &&
      params[key as keyof IPaymentFundsParams] !== ''
    ) {
      ;(acc as Record<string, unknown>)[key] =
        params[key as keyof IPaymentFundsParams]
    }
    return acc
  }, {} as IPaymentFundsParams)
  const response = await commonServerInstance.post(
    `${BASE_URL}/search`,
    filteredParams
  )
  const responseData = response?.data?.data
  return {
    data: responseData?.data as IPaymentFundsResponseData,
    pagination: responseData?.pagination as IPaymentFundsPagination
  }
}

export const addFunds = async (data: IAddFundsPayload): Promise<IPaymentFund> => {
  const formData = new FormData()
  formData.append('partner_id', data.partner_id)
  formData.append('utr', data.utr)
  formData.append('total_amount', data.total_amount.toString())
  if (data.payment_date) {
    formData.append('payment_date', data.payment_date)
  }
  if (data.file) {
    formData.append('file', data.file)
  }

  const response = await commonServerInstance.post(BASE_URL, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response?.data?.data
}

export const updateFunds = async (
  id: number | string,
  data: Partial<IAddFundsPayload>
): Promise<IPaymentFund> => {
  const formData = new FormData()
  if (data.partner_id !== undefined)
    formData.append('partner_id', data.partner_id)
  if (data.utr) formData.append('utr', data.utr)
  if (data.total_amount !== undefined) {
    formData.append('total_amount', data.total_amount.toString())
  }
  if (data.payment_date) formData.append('payment_date', data.payment_date)
  if (data.file) formData.append('file', data.file)

  const response = await commonServerInstance.post(
    `${BASE_URL}/${id}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response?.data?.data
}

export const deleteFunds = async (id: number | string): Promise<void> => {
  const response = await commonServerInstance.delete(`${BASE_URL}/${id}`)
  return response?.data
}

export const fetchOpenInvoices = async (
  paymentId: number | string,
  page: number = 1,
  perPage: number = 10
): Promise<IOpenInvoicesResponse> => {
  const response = await commonServerInstance.get(
    `${BASE_URL}/${paymentId}/invoices`,
    { params: { page, per_page: perPage } }
  )
  return response?.data?.data
}

const ALLOCATIONS_BASE_URL = '/api/payment-allocations'

export const confirmAllocations = async (
  data: IPrepareAllocationsPayload
): Promise<IAllocationResult> => {
  const response = await commonServerInstance.post(
    `${ALLOCATIONS_BASE_URL}/submit`,
    data
  )
  return response?.data?.data
}

// ─── Allocation History ──────────────────────────────────────────────

export const fetchAllocationHistory = async (
  params: IAllocationHistoryParams
): Promise<IAllocationHistoryResponse> => {
  const filteredParams = Object.keys(params).reduce((acc, key) => {
    const value = params[key as keyof IAllocationHistoryParams]
    if (value !== undefined && value !== null && value !== '') {
      ;(acc as Record<string, unknown>)[key] = value
    }
    return acc
  }, {} as IAllocationHistoryParams)

  const response = await commonServerInstance.post(
    `${ALLOCATIONS_BASE_URL}/search`,
    filteredParams
  )
  const responseData = response?.data?.data
  return {
    data: { allocations: responseData?.data || [] },
    pagination: responseData?.pagination as IPaymentFundsPagination
  }
}

export const exportAllocationHistory = async (
  params: IExportAllocationHistoryParams
): Promise<any> => {
  const filteredParams = Object.keys(params).reduce(
    (acc, key) => {
      const value = params[key as keyof IExportAllocationHistoryParams]
      if (value !== undefined && value !== null && value !== '') {
        ;(acc as Record<string, unknown>)[key] = value
      }
      return acc
    },
    {} as Record<string, unknown>
  )

  const response = await downloadInstance.get(
    `${ALLOCATIONS_BASE_URL}/export`,
    { params: filteredParams, responseType: 'blob' }
  )
  return downloadFile(response, 'allocation-history-export.csv')
}

// ─── Payment Funds Export ────────────────────────────────────────────

export const exportPaymentFunds = async (
  params: IExportPaymentFundsParams
): Promise<any> => {
  const filteredParams = Object.keys(params).reduce((acc, key) => {
    const value = params[key as keyof IExportPaymentFundsParams]
    if (value !== undefined && value !== null && value !== '') {
      ;(acc as Record<string, unknown>)[key] = value
    }
    return acc
  }, {} as IExportPaymentFundsParams)

  const response = await downloadInstance.post(
    `${BASE_URL}/export`,
    filteredParams,
    {
      responseType: 'blob'
    }
  )
  return downloadFile(response, 'payment-proofs-export.csv')
}
